/* eslint-disable fp/no-mutation */

/**
 * Create Credential Modal
 * Modal for creating new credentials
 * All fields including file upload are required
 */

import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { useForm } from '@tanstack/react-form'
import { z } from 'zod'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  Check as SubmitIcon,
  Cancel as CancelIcon,
  CloudUpload as UploadIcon,
  InsertDriveFile as UploadFileIcon,
  Close as ClearIcon,
} from '@mui/icons-material'
import axios, { pluckData } from 'src/utilities/http-client'
import {
  getZodValidationError,
  shouldShowZodFieldError,
} from 'src/v3/utilities/validation'
import type { CredentialFileData } from '../../types'
import { CREDENTIAL_TYPES } from '../../types'

dayjs.extend(utc)

interface CreateCredentialModalProps {
  isOpen: boolean
  userID: string
  onClose: () => void
  onSuccess: () => void
  onFailure: (error: string) => void
}

export let CreateCredentialModal = ({
  isOpen,
  userID,
  onClose,
  onSuccess,
  onFailure,
}: CreateCredentialModalProps) => {
  let [fileToUpload, setFileToUpload] = useState<
    CredentialFileData | undefined
  >()
  let [fileError, setFileError] = useState<string>('')
  let [formErrorMessage, setFormErrorMessage] = useState<string>('')

  let updateCredentialImage = (data: {
    id: string
    userID?: string
    imageType: string
  }) =>
    axios.put<string>(`/api/v2/manage/credential/image`, data).then(pluckData)

  let validationSchema = z
    .object({
      credentialIDNumber: z
        .string()
        .nonempty('Credential ID Number is required'),
      issueDate: z.string(),
      expiration: z.string().nonempty('Expiration date is required'),
      type: z.string().nonempty('Credential Type is required'),
      customType: z.string(),
    })
    .refine(
      (data) => {
        if (data.expiration) {
          return dayjs(data.expiration, 'MM/DD/YYYY', true).isValid()
        }
        return true
      },
      {
        message: 'Expiration date is invalid',
        path: ['expiration'],
      },
    )
    .refine(
      (data) => {
        if (data.type === 'Other') {
          return data.customType.trim().length > 0
        }
        return true
      },
      {
        message: 'Custom Credential Type is required',
        path: ['customType'],
      },
    )

  let form = useForm({
    defaultValues: {
      credentialIDNumber: '',
      issueDate: '',
      expiration: '',
      type: '',
      customType: '',
    },
    validators: {
      onMount: validationSchema,
      onChange: validationSchema,
    },
    onSubmit: async ({ value }) => {
      // Validate file is selected
      if (!fileToUpload) {
        setFileError('File is required when creating a credential')
        setFormErrorMessage(
          'Please correct the errors in the form before submitting.',
        )
        return
      }

      setFileError('')
      setFormErrorMessage('')

      // Create credential
      await axios
        .post('/api/v2/manage/credential', {
          userID,
          credentialIDNumber: value.credentialIDNumber,
          issueDate: value.issueDate,
          expiration: value.expiration,
          type:
            value.type === 'Other' ? { other: value.customType } : value.type,
        })
        .then(async ({ data }) => {
          // Upload file with returned credential ID
          let response = await updateCredentialImage({
            id: data.id,
            userID,
            imageType: fileToUpload.extension,
          })

          await axios.put(response, fileToUpload.file, {
            headers: {
              'Cache-Control': 'max-age=2592000',
              'content-type': fileToUpload.type,
            },
          })
        })
        .then(() => {
          form.reset()
          setFileToUpload(undefined)
          setFileError('')
          onSuccess()
          onClose()
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('Error creating credential:', err)
          onFailure('Failed to create credential')
        })
    },
  })

  // Handle file upload
  let handleCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    let file = e.target.files?.[0]
    if (!file) return

    let fileName = file.name
    let reader = new FileReader()
    reader.onloadend = () => {
      let blobData = new Blob([reader.result as ArrayBuffer], {
        type: file.type,
      })
      let fileExtension =
        file.type === 'application/pdf'
          ? 'pdf'
          : file.type === 'image/jpeg'
            ? 'jpg'
            : 'png'

      setFileToUpload({
        name: fileName,
        type: file.type,
        extension: fileExtension,
        file: blobData,
      })
      setFileError('')
    }
    reader.readAsArrayBuffer(file)
  }

  // Handle modal close
  let handleClose = () => {
    form.reset()
    setFileToUpload(undefined)
    setFileError('')
    setFormErrorMessage('')
    onClose()
  }

  useEffect(() => {
    if (isOpen) {
      form.update()
      form.validate('mount')
    }
  }, [isOpen])

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      aria-labelledby="create-credential-dialog-title"
      aria-describedby={
        formErrorMessage
          ? 'form-error-summary'
          : 'create-credential-description'
      }
      maxWidth="xs"
    >
      <Paper className="px-4 py-2">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
          aria-label="Create credential form"
        >
          <DialogTitle
            id="create-credential-dialog-title"
            className="text-center"
          >
            Create Credential
          </DialogTitle>
          <DialogContent className="flex flex-col gap-4 px-2 pb-6">
            <Typography
              id="create-credential-description"
              variant="body2"
              sx={{ display: 'none' }}
            >
              Form to create a new credential. All fields are required.
            </Typography>
            {formErrorMessage && (
              <Box
                id="form-error-summary"
                role="alert"
                aria-live="assertive"
                sx={{
                  p: 2,
                  bgcolor: 'error.light',
                  color: 'error.contrastText',
                }}
                className="rounded"
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  {formErrorMessage}
                </Typography>
              </Box>
            )}
            <form.Field name="credentialIDNumber">
              {(field) => (
                <TextField
                  className="mt-2"
                  variant="outlined"
                  size="small"
                  label="Credential ID Number"
                  type="text"
                  fullWidth
                  required
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  error={shouldShowZodFieldError(field.state.meta)}
                  helperText={
                    shouldShowZodFieldError(field.state.meta) &&
                    getZodValidationError(field.state.meta.errors)
                  }
                />
              )}
            </form.Field>

            <form.Field
              name="issueDate"
              validators={{
                onChangeListenTo: ['expiration'],
                onChange: ({ value, fieldApi }) =>
                  !!value &&
                  !!fieldApi.form.getFieldValue('expiration') &&
                  !dayjs(value).isBefore(
                    fieldApi.form.getFieldValue('expiration'),
                  )
                    ? 'Credential Issue Date must be prior to its Expiration'
                    : undefined,
              }}
            >
              {(field) => (
                <Box>
                  <Box className="flex justify-center">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Issue Date"
                        value={
                          field.state.value
                            ? dayjs.utc(field.state.value)
                            : null
                        }
                        onChange={(val) =>
                          field.handleChange(
                            val ? dayjs.utc(val).format('MM/DD/YYYY') : '',
                          )
                        }
                        slotProps={{
                          textField: {
                            required: true,
                            error: shouldShowZodFieldError(field.state.meta),
                            helperText:
                              shouldShowZodFieldError(field.state.meta) &&
                              getZodValidationError(field.state.meta.errors),
                            variant: 'outlined',
                            size: 'small',
                            fullWidth: true,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Box>
              )}
            </form.Field>

            <form.Field name="expiration">
              {(field) => (
                <Box>
                  <Box className="flex justify-center">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Expiration"
                        value={
                          field.state.value
                            ? dayjs.utc(field.state.value)
                            : null
                        }
                        onChange={(val) =>
                          field.handleChange(
                            val ? dayjs.utc(val).format('MM/DD/YYYY') : '',
                          )
                        }
                        slotProps={{
                          textField: {
                            required: true,
                            error: shouldShowZodFieldError(field.state.meta),
                            helperText:
                              shouldShowZodFieldError(field.state.meta) &&
                              getZodValidationError(field.state.meta.errors),
                            variant: 'outlined',
                            size: 'small',
                            fullWidth: true,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Box>
              )}
            </form.Field>

            <form.Field name="type">
              {(field) => (
                <FormControl
                  fullWidth
                  required
                  error={shouldShowZodFieldError(field.state.meta)}
                >
                  <InputLabel size="small">Credential Type</InputLabel>
                  <Select
                    value={field.state.value}
                    label="Credential Type"
                    onChange={(e) => field.handleChange(e.target.value)}
                    variant="outlined"
                    size="small"
                  >
                    {CREDENTIAL_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                  {shouldShowZodFieldError(field.state.meta) && (
                    <Typography
                      variant="caption"
                      color="error"
                      className="mt-1 ml-3.5"
                    >
                      {getZodValidationError(field.state.meta.errors)}
                    </Typography>
                  )}
                </FormControl>
              )}
            </form.Field>

            <form.Subscribe
              selector={(state) => [state.values.type]}
              children={([type]) =>
                type === 'Other' && (
                  <form.Field name="customType">
                    {(field) => (
                      <TextField
                        variant="outlined"
                        size="small"
                        label="Custom Credential Type"
                        type="text"
                        fullWidth
                        required
                        value={field.state.value || ''}
                        onChange={(e) => field.handleChange(e.target.value)}
                        error={shouldShowZodFieldError(field.state.meta)}
                        helperText={
                          shouldShowZodFieldError(field.state.meta) &&
                          getZodValidationError(field.state.meta.errors)
                        }
                      />
                    )}
                  </form.Field>
                )
              }
            />

            <Box>
              <label htmlFor="create-credential-file-upload">
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  fullWidth
                  color={fileError ? 'error' : 'primary'}
                  aria-describedby={
                    fileError
                      ? 'file-upload-error file-upload-helper'
                      : 'file-upload-helper'
                  }
                >
                  <input
                    id="create-credential-file-upload"
                    accept=".png,.jpg,.jpeg,.pdf"
                    style={{ display: 'none' }}
                    type="file"
                    onChange={handleCapture}
                    aria-label="Select credential file (required)"
                    aria-required="true"
                    aria-invalid={fileError ? 'true' : 'false'}
                    aria-describedby={
                      fileError
                        ? 'file-upload-error file-upload-helper'
                        : 'file-upload-helper'
                    }
                  />
                  Select File *
                </Button>
              </label>
              {fileError && (
                <Typography
                  id="file-upload-error"
                  variant="caption"
                  color="error"
                  className="block mt-1 text-center"
                  role="alert"
                >
                  {fileError}
                </Typography>
              )}
              {fileToUpload && (
                <Box className="flex items-center justify-center gap-2 mt-2">
                  <Typography variant="subtitle1" className="flex items-center">
                    <UploadFileIcon
                      color="primary"
                      sx={{ mr: '2px', mb: '2px' }}
                    />
                    {fileToUpload.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setFileToUpload(undefined)}
                    aria-label="Clear file selection"
                    sx={{ color: 'text.secondary' }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
            <Typography
              id="file-upload-helper"
              variant="caption"
              color="textSecondary"
            >
              * Allowed file types: png, jpg, pdf
              <br />* File upload is required when creating a new credential.
            </Typography>
          </DialogContent>

          <DialogActions
            disableSpacing={true}
            className="flex-col gap-2 pt-0 pb-6 mt-0"
          >
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    fullWidth
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={20} />
                      ) : (
                        <SubmitIcon />
                      )
                    }
                    disabled={!canSubmit || isSubmitting}
                  >
                    Submit
                  </Button>
                  <Button
                    onClick={handleClose}
                    color="error"
                    variant="contained"
                    fullWidth
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                </>
              )}
            />
          </DialogActions>
        </form>
      </Paper>
    </Dialog>
  )
}

export default CreateCredentialModal
